import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { LibraryItemHoverButton } from '@/components/Library/LibraryItemHoverButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { EditableTitle } from '@/components/Title/EditableTitle';
import { toast } from '@/components/toast';
import { VideoFeedback } from '@/components/VideoFeedback/VideoFeedback';
import { UpgradeNeededTooltipContent } from '@/components/Voice/VoiceModal/UpgradeNeededTooltipContent';
import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import FlashLightning from '@/images/FlashLightning.svg';
import { useUpdateAvatarName } from '@/pages/avatar';
import { AvatarUsePopover } from '@/pages/avatar/components/AvatarTile/components/AvatarUsePopover';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { useFlag, useFlagExperiment } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import theme from '@/theme';
import { MixPanelEvent, StyleCollection } from '@/utils/types';
import { isExpressPresenter, isPremiumPresenter, PlanGroup, Presenter } from '@d-id/types';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogTitle, Tooltip } from '@mui/material';
import mixpanel from 'mixpanel-browser';
import React, { useCallback, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { AdvancedSettings } from './AdvancedSettings';

const AvatarName = styled.div`
    width: 100%;
    text-align: center;
    font-size: 20px;
    margin: 15px 0;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 20px;
    white-space: nowrap;
`;

const UseThisAvatarButton = styled(Button)<{ isOnMobile?: boolean }>`
    height: 38px;
    ${props =>
        props.isOnMobile
            ? `
        width: 4rem !important;
        padding: 0 0.7rem 0 2rem;
        min-width: 0;
        `
            : ''}
    ${[theme.breakpoints.down(780)]}: {
        width: 45%;
    }
`;

const StyledDialog = styled(BootstrapDialog)`
    & .MuiPaper-root {
        padding: 20px 38px;
        box-sizing: content-box;
        gap: 22px;
        width: 100%;
        max-width: 1024px;
        max-height: 720px;
        border-radius: 20px;
        overflow: visible;
        height: 90%;

        @media (max-height: 780px) {
            margin: 120px 0;
            background: #f1f2f6;
        }
    }
`;

const StyledEditableTitle = styled(EditableTitle)`
    svg {
        top: unset;
    }
`;

const FlashLightningIcon = styled(MaskedIcon)`
    margin-right: 6px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    margin-left: auto;
    gap: 14px;
    justify-content: flex-end;
`;

const VideoContainer = styled.div`
    height: calc(100% - 166px);
    border-radius: 12px;
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    position: relative;
`;

const StyledVideo = styled.video`
    width: 100%;
    height: 100%;
    ::cue {
        text-shadow:
            -1px -1px 0 #000,
            1px -1px 0 #000,
            -1px 1px 0 #000,
            1px 1px 0 #000;
    }
`;

export const styles: StyleCollection = {
    svgFill: {
        height: '100%',
        width: '100%',
    },

    actions: {
        gap: '38rem',
        padding: '0 0 1rem 0 !important',
        display: 'flex',
        justifyContent: 'space-between !important',
        width: '100%',
    },
    closeIcon: {
        marginLeft: 'auto',
        height: '4.2rem',
        width: '4.2rem',
        color: (theme: Theme) => theme.colors.primary,
    },
    titlestyle: {
        padding: '0',
    },
    videostyle: {
        overflow: 'hidden',
        width: '100%',
        height: '100%',
    },
    tooltip: {
        sx: {
            backgroundColor: '#FFF',
            color: theme.colors.secondary[900],
            boxShadow:
                '5.3px 13.3px 18.8px -1.2px rgba(187, 187, 187, 0.29), 2.4px 6.1px 8.6px -0.8px rgba(187, 187, 187, 0.23), 1.1px 2.8px 3.9px -0.4px rgba(187, 187, 187, 0.17), 0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.12)',
            padding: '16px',
            borderRadius: '8px',
        },
    },
};

const VideoPlayer = ({
    avatar,
    handleVideoLoaded,
}: {
    avatar: Presenter;
    handleVideoLoaded: React.ComponentProps<typeof StyledVideo>['onLoadedMetadata'];
}) => {
    const [videoFeedbackVisible, setVideoFeedbackVisible] = useState(false);
    const avatarFeedbackEnabled = useFlagExperiment('avatar_feedback_enabled', false);

    const handleSubmit = useCallback(
        ({ starRating, feedback }: { starRating: number; feedback?: string }) => {
            mixpanel.track('avatar-feedback-submit', {
                presenter_id: avatar.id,
                presenter_type: avatar.type,
                from: 'preview',
                rating: starRating,
                feedback,
            });
        },
        [avatar.id, avatar.type]
    );

    const handleStarRatingChange = useCallback(
        (rating: number) => {
            mixpanel.track(`avatar-feedback-${rating}star`, {
                presenter_id: avatar.id,
                presenter_type: avatar.type,
                from: 'preview',
            });
        },
        [avatar.id, avatar.type]
    );

    const handleVideoFeedbackShow = useCallback(() => {
        mixpanel.track('avatar-feedback-show', {
            presenter_id: avatar.id,
            presenter_type: avatar.type,
            from: 'preview',
        });

        setVideoFeedbackVisible(true);
    }, [avatar.id, avatar.type]);

    const handleClose = useCallback(() => {
        setVideoFeedbackVisible(false);
    }, []);

    return (
        <VideoContainer>
            <StyledVideo
                preload="auto"
                onLoadedMetadata={handleVideoLoaded}
                poster={avatar.poster}
                controls={false}
                controlsList="nodownload"
                crossOrigin="anonymous"
                playsInline
                onPlay={handleClose}
                onEnded={handleVideoFeedbackShow}>
                <source src={avatar.talkingPreviewUrl || avatar.previewUrl} type="video/mp4" />
                Your browser does not support the video tag.
            </StyledVideo>
            {videoFeedbackVisible && avatarFeedbackEnabled && (
                <VideoFeedback onClose={handleClose} onSubmit={handleSubmit} onStarRatingChange={handleStarRatingChange} />
            )}
        </VideoContainer>
    );
};

interface Props {
    avatar: Presenter;
    editable?: boolean;
    onModalStateChange?: (isOpen: boolean) => void;
    onPreviewViewed?: () => void;
}

export const AvatarPreviewModal = ({ avatar, editable, onPreviewViewed, onModalStateChange }: Props) => {
    const { user } = useAuth();
    const mixpanel = useAnalytics();
    const [isOpen, setOpen] = useState(false);
    const [avatarName, setAvatarName] = useState<string>(avatar.name);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const canUseAvatar =
        !isPremiumPresenter(avatar) ||
        (user?.planGroup &&
            [PlanGroup.PRO, PlanGroup.ADVANCED, PlanGroup.ENTERPRISE, PlanGroup.ENTERPRISE_TRIAL, PlanGroup.TRIAL, PlanGroup.FREE].includes(
                user.planGroup
            ));
    const { navigateToAdvancedEditor, isLoading } = useNavigateToAdvancedEditor();
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);
    const { mutateAsync: updateAvatarName } = useUpdateAvatarName();

    const handleModalClose = () => {
        setOpen(false);
        onModalStateChange?.(false);
    };

    const handleModalOpen = () => {
        mixpanel?.track('avatar-preview', { event: MixPanelEvent.CLICK });
        setOpen(true);
        onModalStateChange?.(true);
    };

    useEffect(() => {
        if (avatar.name) {
            setAvatarName(avatar.name);
        }
    }, [avatar.name]);

    const handleVideoLoaded = (event: any) => {
        const target = event.target as HTMLVideoElement;
        target.currentTime = 0;
        target.controls = true;
        target.play();
        onPreviewViewed?.();
    };

    const updateName = async (value: string) => {
        mixpanel?.track('avatar-name', { event: MixPanelEvent.CLICK, value, from: 'preview-modal' });

        try {
            await updateAvatarName({ avatar, name: value });
        } catch (e) {
            toast.error({ title: 'Failed to update avatar name, please try again' });
            logger.error('Failed to update avatar name', { e });
        }
    };

    const handleUseAvatarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleUseAvatarInVideo = async () => {
        if (outOfFreeVideos) {
            setUpgradeFreePlanModalOpen(true);
            return;
        }
        await navigateToAdvancedEditor({ options: { initialAvatar: avatar, from: 'Avatars' }, mixPanelFrom: 'avatar-preview' });
    };
    const { id: avatarId } = avatar;
    const changeAdvancedSettingEnabled = useFlag('enable-changing-presenter-attributes', false);

    return (
        <>
            <LibraryItemHoverButton color="outlined" onClick={handleModalOpen} text="Preview" />
            <StyledDialog onClose={handleModalClose} titlestyle={styles.titlestyle} open={isOpen}>
                <DialogTitle>
                    <AvatarName>
                        <StyledEditableTitle
                            fontSize={22}
                            disabled={!editable}
                            capitalize
                            value={avatarName}
                            onChange={setAvatarName}
                            onBlur={updateName}
                        />
                        <IconButton ariaLabel="close" onClick={handleModalClose} sx={styles.closeIcon}>
                            <CloseIcon sx={styles.svgFill} />
                        </IconButton>
                    </AvatarName>
                </DialogTitle>

                <VideoPlayer avatar={avatar} handleVideoLoaded={handleVideoLoaded} />

                <DialogActions sx={styles.actions}>
                    {changeAdvancedSettingEnabled && <AdvancedSettings avatarId={avatarId!} currentA2eModel={avatar.a2e_model} />}
                    <Tooltip
                        title={
                            <UpgradeNeededTooltipContent
                                title="Get the best Avatars"
                                text="Premium Avatars are available on our Pro plan"
                            />
                        }
                        placement="top"
                        disableHoverListener={canUseAvatar}
                        slotProps={styles}>
                        <ButtonsContainer>
                            {isExpressPresenter(avatar) ? (
                                <Button variant="contained" color="primary" onClick={handleUseAvatarInVideo} disabled={isLoading}>
                                    Create video
                                </Button>
                            ) : (
                                <UseThisAvatarButton
                                    data-testid="use-this-avatar-button"
                                    disabled={!canUseAvatar}
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUseAvatarClick}>
                                    {!canUseAvatar && <FlashLightningIcon src={FlashLightning} size="16px" />}
                                    Use this avatar
                                </UseThisAvatarButton>
                            )}
                        </ButtonsContainer>
                    </Tooltip>

                    {canUseAvatar && <AvatarUsePopover avatar={avatar} anchor={anchorEl} setAnchor={setAnchorEl} />}
                </DialogActions>
            </StyledDialog>
        </>
    );
};
